<template>
  <div class="mini-tab-content">
    <div
      v-if="loadingAssessments"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="info"
      />
      <span>{{ $t('involvedPanel.assessment.toolbar.loading') }}</span>
    </div>
    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="ClipboardIcon"
            size="13"
            class="mr-25 text-info"
          />
          {{ assessmentCountLabel }}
        </span>
        <button
          class="btn btn-sm btn-info"
          @click="openAssessmentModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.assessment.toolbar.newButton') }}
        </button>
      </div>

      <div
        v-if="!assessments.length"
        class="interc-empty"
      >
        <feather-icon
          icon="ClipboardIcon"
          size="26"
        />
        <p>{{ $t('involvedPanel.assessment.empty.noInitialAssessments') }}</p>
      </div>

      <div
        v-if="assessments.length"
        class="interc-list"
      >
        <div
          v-for="av in assessments"
          :key="av.id"
          :class="['interc-item', { 'interc-item--cancelled': av.cancelled_by_user_id }]"
        >
          <div class="interc-item__top">
            <div
              class="d-flex align-items-center"
              style="gap: 6px; flex-wrap: wrap"
            >
              <b-badge
                :variant="av.origin === 'mobile' ? 'success' : 'primary'"
                style="font-size: 10px"
              >
                <feather-icon
                  :icon="av.origin === 'mobile' ? 'SmartphoneIcon' : 'MonitorIcon'"
                  size="10"
                  class="mr-25"
                />
                {{ av.origin === 'mobile' ? $t('involvedPanel.assessment.item.badge.mobile') : $t('involvedPanel.assessment.item.badge.web') }}
              </b-badge>
              <span
                v-if="av.cancelled_by_user_id"
                class="badge badge-danger"
                style="font-size: 10px"
              >{{ $t('involvedPanel.assessment.item.badge.cancelled') }}</span>
            </div>
            <button
              v-if="!av.cancelled_by_user_id"
              class="interc-item__del"
              :title="$t('involvedPanel.assessment.item.cancelTooltip')"
              @click="cancelAssessment(av.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </button>
          </div>

          <div
            v-if="av.answers && av.answers.length"
            class="mt-1"
          >
            <div
              v-for="group in groupedAnswers(av.answers)"
              :key="group.questionId"
              class="assessment-answer-row"
            >
              <span class="assessment-answer-label">{{ group.questionLabel }}:</span>
              <span
                class="assessment-answer-value"
                :class="{ 'text-muted': av.cancelled_by_user_id }"
              >{{ group.value }}</span>
            </div>
          </div>

          <div class="interc-item__meta mt-1">
            <feather-icon
              icon="UserIcon"
              size="11"
            />
            <span>{{ av.user ? av.user.name : '—' }}</span>
            <span class="interc-item__dot" />
            <feather-icon
              icon="ClockIcon"
              size="11"
            />
            <span>{{ av.created_at | formatDate }}</span>
          </div>
          <div
            v-if="av.cancelled_by_user_id"
            class="interc-item__cancelled-row"
          >
            <feather-icon
              icon="XCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ $t('involvedPanel.assessment.item.cancelledBy') }}
            <strong>{{ av.cancelled_by ? av.cancelled_by.name : '—' }}</strong>
            · {{ av.cancelled_at | formatDate }}
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showAssessmentModal"
      :title="$t('involvedPanel.assessment.modal.title')"
      size="lg"
      centered
      no-close-on-backdrop
      @hidden="resetAssessmentForm"
    >
      <div
        v-if="loadingAssessmentQuestions"
        class="interc-loading"
      >
        <b-spinner
          small
          variant="info"
        />
        <span>{{ $t('involvedPanel.assessment.modal.loading') }}</span>
      </div>
      <div
        v-else-if="!assessmentQuestions.length"
        class="text-center text-muted py-3"
      >
        {{ $t('involvedPanel.assessment.modal.noQuestions') }}
      </div>
      <div
        v-else
        class="assessment-grid"
      >
        <div
          v-for="q in assessmentQuestions"
          :key="q.id"
          class="assessment-question-card"
        >
          <div class="assessment-question-label">
            {{ q.label }}
          </div>

          <div v-if="q.type === 'single_select'">
            <div
              v-for="opt in q.options"
              :key="opt.id"
              class="assessment-option"
            >
              <label
                class="d-flex align-items-center mb-0"
                style="cursor: pointer; gap: 6px"
              >
                <input
                  v-model="assessmentAnswers[String(q.id)]"
                  type="radio"
                  :name="'q_' + q.id"
                  :value="opt.id"
                >
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <div v-else-if="q.type === 'multiple_select'">
            <div
              v-for="opt in q.options"
              :key="opt.id"
              class="assessment-option"
            >
              <label
                class="d-flex align-items-center mb-0"
                style="cursor: pointer; gap: 6px"
              >
                <input
                  v-model="assessmentAnswers[String(q.id)]"
                  type="checkbox"
                  :value="opt.id"
                >
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <div v-else-if="q.type === 'text'">
            <input
              v-model="assessmentAnswers[String(q.id)]"
              type="text"
              class="form-control form-control-sm"
              placeholder="—"
            >
          </div>
        </div>
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="resetAssessmentForm(); showAssessmentModal = false"
        >
          {{ $t('involvedPanel.assessment.modal.clearButton') }}
        </button>
        <button
          class="btn btn-sm btn-info"
          :disabled="savingAssessment || !assessmentQuestions.length"
          @click="saveAssessment"
        >
          <b-spinner
            v-if="savingAssessment"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="13"
            class="mr-25"
          />
          {{ $t('involvedPanel.assessment.modal.saveButton') }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'AssessmentPanel',
  mixins: [involvedPanelMixin],
  data() {
    return {
      assessments: [],
      loadingAssessments: false,
      assessmentQuestions: [],
      loadingAssessmentQuestions: false,
      showAssessmentModal: false,
      savingAssessment: false,
      assessmentAnswers: {},
    }
  },
  computed: {
    assessmentCountLabel() {
      if (this.assessments.length) {
        return this.$t('involvedPanel.assessment.toolbar.assessmentCount', { count: this.assessments.length })
      }
      return this.$t('involvedPanel.assessment.toolbar.noAssessments')
    },
  },
  mounted() {
    this.loadAssessments()
  },
  methods: {
    async loadAssessments() {
      this.loadingAssessments = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_initial_assessments`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.assessments = res.data.data || []
      } catch (e) {
        this.assessments = []
        this.$message(this.$t('involvedPanel.assessment.messages.errorTitle'), this.$t('involvedPanel.assessment.messages.loadError'), 'error')
      } finally {
        this.loadingAssessments = false
      }
    },
    async openAssessmentModal() {
      this.showAssessmentModal = true
      if (!this.assessmentQuestions.length) {
        this.loadingAssessmentQuestions = true
        try {
          const res = await this.$http.get(
            `${this.$store.state.api}initial_assessment_questions/for-occurrence/${this.occurrenceId}`,
          )
          this.assessmentQuestions = res.data.data || []
          this.resetAssessmentForm()
        } catch (e) {
          this.$message(this.$t('involvedPanel.assessment.messages.errorTitle'), this.$t('involvedPanel.assessment.messages.questionsLoadError'), 'error')
        } finally {
          this.loadingAssessmentQuestions = false
        }
      } else {
        this.resetAssessmentForm()
      }
    },
    resetAssessmentForm() {
      this.assessmentAnswers = {}
      this.assessmentQuestions.forEach(q => {
        this.$set(this.assessmentAnswers, String(q.id), q.type === 'multiple_select' ? [] : null)
      })
    },
    async saveAssessment() {
      const answers = []
      this.assessmentQuestions.forEach(q => {
        const val = this.assessmentAnswers[String(q.id)]
        if (q.type === 'text' && val && String(val).trim()) {
          answers.push({ question_id: q.id, answer_text: String(val).trim() })
        } else if (q.type === 'single_select' && val !== null && val !== undefined) {
          answers.push({ question_id: q.id, option_ids: [Number(val)] })
        } else if (q.type === 'multiple_select' && Array.isArray(val) && val.length) {
          answers.push({ question_id: q.id, option_ids: val.map(Number) })
        }
      })
      if (!answers.length) {
        this.$message(this.$t('involvedPanel.assessment.messages.warningTitle'), this.$t('involvedPanel.assessment.messages.noAnswersWarning'), 'warning')
        return
      }
      this.savingAssessment = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrence_initial_assessments`, {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          answers,
        })
        const saved = res.data.data || res.data
        this.assessments.unshift(saved)
        this.showAssessmentModal = false
        this.$message(this.$t('involvedPanel.assessment.messages.saveSuccessTitle'), this.$t('involvedPanel.assessment.messages.saveSuccess'), 'success')
      } catch (e) {
        const msg = e.response && e.response.data ? JSON.stringify(e.response.data) : this.$t('involvedPanel.assessment.messages.saveError')
        this.$message(this.$t('involvedPanel.assessment.messages.errorTitle'), msg, 'error')
      } finally {
        this.savingAssessment = false
      }
    },
    async cancelAssessment(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('involvedPanel.assessment.confirm.cancel.message'),
        {
          title: this.$t('involvedPanel.assessment.confirm.cancel.title'), okTitle: this.$t('involvedPanel.assessment.confirm.cancel.okTitle'), cancelTitle: this.$t('involvedPanel.assessment.confirm.cancel.cancelTitle'), okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_initial_assessments/${id}`)
        const updated = res.data.data || res.data
        const idx = this.assessments.findIndex(a => a.id === id)
        if (idx !== -1) this.$set(this.assessments, idx, updated)
      } catch (e) {
        this.$message(this.$t('involvedPanel.assessment.messages.errorTitle'), this.$t('involvedPanel.assessment.messages.cancelError'), 'error')
      }
    },
    groupedAnswers(answers) {
      const map = {}
      answers.forEach(a => {
        const qId = a.question_id
        const qLabel = a.question ? a.question.label : `Q${qId}`
        if (!map[qId]) map[qId] = { questionId: qId, questionLabel: qLabel, values: [] }
        if (a.answer_text) {
          map[qId].values.push(a.answer_text)
        } else if (a.option) {
          map[qId].values.push(a.option.label)
        }
      })
      return Object.values(map).map(g => ({ ...g, value: g.values.join(', ') || '—' }))
    },
  },
}
</script>
